import cn from 'classnames';

import Tag from 'components/common/Tag';
import {
  ANDROID_APK_URL,
  APP_STORE_URL,
  DOCS_URL,
  IS_DEMO,
  IS_INDIAN_EXCHANGE,
  IS_TESTNET_LINK_IN_INDIAN_EXCHANGE,
  PLATFORM_STATUS_URL,
  PLAY_STORE_URL,
  SUPPORT_CENTER_LINK_HOME,
  SUPPORT_TICKET_URL,
  TAX_INFO_LINK,
  TESTNET_URL,
} from 'constants/constants';
import { SIGNAL_TRADING_FEATURE_ENABLED } from 'constants/featureFlag';
import routes from 'constants/routes';
import { SIGNAL_TRADING_TUTORIAL_BLOG, USER_GUIDE_URL } from 'constants/urls';
import i18n from 'i18n/config';
import { userSelector } from 'selectors/otherSelectors';
import { useAppSelector } from 'storeHooks';
import AppStore from 'styles/assets/appStoreBadge.png';
import DownloadAndroid from 'styles/assets/downloadandroid.svg';
import GooglePlay from 'styles/assets/googlePlayBadge.png';
import {
  ApiDocsIcon,
  BlogIcon,
  ContractSpecificationIcon,
  GiftIcon,
  InsuranceFundIcon,
  MockTrading,
  RaiseSupportIcon,
  SettlementPrices,
  StatusIcon,
  SupportCenterIcon,
  TradingFeesIcon,
  UserGuideIcon,
} from 'styles/assets/icons';
import LineChartBox from 'styles/assets/icons/LineChartBox';
import SignalTradingIcon from 'styles/assets/icons/SignalTradingIcon';
import TaxInfoIcon from 'styles/assets/icons/TaxInfoIcon';
import TradeData from 'styles/assets/icons/TradeData';
import Box from 'UIKit/Box';

import classes from './MenuSection.module.scss';
import { Menu } from './MenuSection.types';

const textNew = i18n.t('headerFooter:header.notification.new');

const useResourcesMenus = () => {
  const user = useAppSelector(userSelector);
  const isUserAvailable = user.id;

  return [
    {
      id: 'resources_1',
      label: 'Trading Fees',
      link: `/fees`,
      icon: <TradingFeesIcon height={20} width={20} className="left-icon" />,
      target: '_self',
      rel: '',
      key: 'trading_fees',
    },
    {
      id: 'resources_2',
      label: 'API Docs',
      link: `${DOCS_URL}/#introduction`,
      icon: <ApiDocsIcon height={20} width={20} className="left-icon" />,
      target: '_self',
      rel: '',
      key: 'api_docs',
    },
    {
      id: 'resources_3',
      label: 'Contract Specifications',
      link: `/contracts`,
      icon: <ContractSpecificationIcon height={20} width={20} className="left-icon" />,
      target: '_self',
      rel: '',
      key: 'contract_specifications',
    },
    {
      id: 'resources_4',
      label: 'Trade Data',
      link: `/app${routes.tradeHistory}`,
      icon: (
        <TradeData
          fill1="var(--dropdownIconColor)"
          fill2="var(--dropdownIconColor)"
          fill3="var(--dropdownIconColor)"
          width={20}
          height={20}
          className={classes.tradeHistoryIcon}
        />
      ),
      target: '_self',
      rel: '',
      key: 'trade_history',
    },
    {
      id: 'resources_5',
      label: 'Blog',
      link: 'https://delta.exchange/blog',
      icon: <BlogIcon height={20} width={20} className="left-icon" />,
      target: '_blank',
      rel: '',
      key: 'blog',
    },
    {
      id: 'resources_6',
      label: 'Settlement Prices',
      link: `/app/expired_futures_and_options`,
      icon: <SettlementPrices height={20} width={20} className="left-icon" />,
      target: '_self',
      rel: '',
      key: 'settlement_prices',
    },
    {
      id: 'resources_7',
      label: 'Platform Status',
      link: PLATFORM_STATUS_URL,
      icon: <StatusIcon height={20} width={20} className="left-icon" />,
      target: '_blank',
      rel: '',
      key: 'platform_status',
    },
    {
      id: 'resources_8',
      label: 'Insurance Fund',
      link: `/insurance-fund`,
      icon: <InsuranceFundIcon height={20} width={20} className="left-icon" />,
      target: '_self',
      rel: '',
      key: 'insurance_fund',
      hide: IS_INDIAN_EXCHANGE,
    },
    {
      id: 'resources_9',
      label: 'Offers',
      link: `/offers`,
      icon: (
        <GiftIcon
          height={20}
          width={20}
          className={cn('left-icon', classes.offersIcon)}
        />
      ),
      target: '_self',
      rel: '',
      key: 'offers',
    },
    {
      id: 'resources_10',
      label: 'Analytics',
      link: `/app${routes.optionsAnalyticsDashboard.default}`,
      icon: (
        <LineChartBox
          height={20}
          width={20}
          className={cn('left-icon', classes.optionsAnalyticsIcon)}
        />
      ),
      target: '_self',
      rel: '',
      key: 'options_analytics',
    },
    {
      id: 'resources_11',
      label: 'Trading Bot',
      link: isUserAvailable
        ? `/app${routes.account.signalTrading}`
        : SIGNAL_TRADING_TUTORIAL_BLOG,
      hide: !SIGNAL_TRADING_FEATURE_ENABLED,
      icon: (
        <Box className={classes.newTagWrapper}>
          <Tag text={textNew} className={classes.newTag} />
          <SignalTradingIcon className={classes.signalTradingIcon} />
        </Box>
      ),
      target: isUserAvailable ? '_self' : '_blank',
      rel: '',
      key: 'signal_trading',
    },
    {
      id: 'resources_13',
      label: 'Demo Trading',
      link: TESTNET_URL,
      icon: <MockTrading className="left-icon" />,
      target: '_blank',
      rel: '',
      key: 'mocktrading',
      hide: IS_DEMO || !IS_TESTNET_LINK_IN_INDIAN_EXCHANGE,
    },
  ] as const;
};

const HELP_MENUS: readonly Menu[] = [
  {
    id: 'help_1',
    label: 'Raise Support Ticket',
    link: SUPPORT_TICKET_URL,
    icon: <RaiseSupportIcon className="left-icon" />,
    target: '_blank',
    rel: 'noopener noreferrer',
    key: 'raise_support_ticket',
  },
  {
    id: 'help_2',
    label: 'User Guide',
    link: USER_GUIDE_URL,
    icon: <UserGuideIcon className="left-icon" />,
    target: '_blank',
    rel: 'noopener noreferrer',
    key: 'user_guide',
  },
  {
    id: 'help_3',
    label: 'Support Center',
    link: SUPPORT_CENTER_LINK_HOME,
    icon: <SupportCenterIcon className="left-icon" />,
    target: '_blank',
    rel: '',
    key: 'support_center',
  },
  {
    id: 'help_4',
    label: 'Tax Info',
    link: TAX_INFO_LINK,
    icon: (
      <TaxInfoIcon
        height={20}
        width={20}
        className={cn(classes.hoverIcon, classes.taxIcon)}
      />
    ),
    target: '_blank',
    rel: '',
    key: 'tax_file',
  },
] as const;

const APP_DOWNLOAD_MENUS: readonly Menu[] = [
  {
    id: 'app_download_1',
    link: APP_STORE_URL as string,
    children: <img
      src={AppStore}
      width="110px"
      height="32px"
      alt="app-store"
      data-palette="APP_DOWNLOAD_MENUS" />,
    target: '_blank',
    rel: 'noreferrer',
    key: 'app_store_link',
  },
  {
    id: 'app_download_2',
    link: PLAY_STORE_URL,
    children: <img
      src={GooglePlay}
      width="110px"
      height="32px"
      alt="google-play"
      data-palette="APP_DOWNLOAD_MENUS" />,
    target: '_blank',
    rel: 'noreferrer',
    key: 'play_store_link',
  },
  {
    id: 'app_download_3',
    link: ANDROID_APK_URL,
    children: (
      <img
        src={DownloadAndroid}
        alt="android-app-download"
        width="110px"
        height="32px"
        data-palette="APP_DOWNLOAD_MENUS" />
    ),
    target: '_blank',
    rel: 'noreferrer',
    key: 'android_app_link',
  },
] as const;

export { APP_DOWNLOAD_MENUS, HELP_MENUS, useResourcesMenus };
